import { FC, Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetRef, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { BimTreeNode, BimViewerHandle } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { elementTreeKey } from '../../useBim3DTools';
import { NodeTitle, spatialLabel } from './components/NodeTitle';
import { INodeVisibility, NodeVisibility, NodeVisibilityProvider } from './NodeVisibilityContext';
import { TreeWrapper, EmptyState } from './styles';

interface IBimSpatialTreeProps {
  tree: BimTreeNode[];
  /** O data model chega depois do render — o painel distingue esperar de não ter. */
  state: 'loading' | 'ready' | 'unavailable';
  expandedKeys: string[];
  /** Destacados. O primeiro é revelado: o grupo carrega e a lista rola até ele. */
  selectedKeys?: string[];
  search: string;
  handle: BimViewerHandle | null;
  /**
   * Modelo desta árvore na cena federada. Sem ele, as folhas de elemento saem do
   * data model errado — cada modelo federado tem o seu, e os expressId são
   * locais a cada arquivo.
   */
  modelIndex?: number;
  /** Ocultos por nó (chave → quantos), calculado uma vez por mudança. */
  hiddenCountByKey: Map<string, number>;
  /** Só para as folhas de elemento, que não estão no mapa acima. */
  hiddenIds: Set<number>;
  onExpandedKeysChange(keys: string[]): void;
  onSelectNode(node: BimTreeNode): void;
  onToggleVisibility(node: BimTreeNode): void;
  /** Nós isolados pelo filtro da linha. */
  isolatedKeys?: string[];
  onToggleIsolation(node: BimTreeNode): void;
}

/** Mantido em sincronia com o `padding` do `TreeWrapper` (styles.ts). */
const TREE_PADDING = 16;

/** O grupo contém o elemento (ou a peça) desta chave de folha? */
const groupHas = (group: BimTreeNode, key: string): boolean =>
  !!group.expressIds?.some((id) => elementTreeKey(id) === key)
  || Object.values(group.parts ?? {}).some((parts) => parts.some((id) => elementTreeKey(id) === key));

const matches = (node: BimTreeNode, term: string): boolean =>
  spatialLabel(node).toLowerCase().includes(term) || node.ifcType.toLowerCase().includes(term);

/** Mantém só os ramos que casam com a busca (um pai sobrevive por um filho). */
const filterTree = (nodes: BimTreeNode[], term: string): BimTreeNode[] =>
  nodes.reduce<BimTreeNode[]>((acc, node) => {
    const children = filterTree(node.children, term);
    if (children.length > 0 || matches(node, term)) {
      acc.push({ ...node, children });
    }
    return acc;
  }, []);

export const BimSpatialTree: FC<IBimSpatialTreeProps> = ({
  tree,
  state,
  expandedKeys,
  selectedKeys,
  search,
  handle,
  modelIndex,
  hiddenCountByKey,
  hiddenIds,
  onExpandedKeysChange,
  onSelectNode,
  onToggleVisibility,
  isolatedKeys = [],
  onToggleIsolation,
}) => {
  const { t } = useTranslation('viewer');
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const treeRef = useRef<GetRef<typeof Tree>>(null);
  const [height, setHeight] = useState(0);
  // Folhas de elemento resolvidas sob demanda: chave do grupo → nós já nomeados.
  const [elementNodes, setElementNodes] = useState<Record<string, BimTreeNode[]>>({});

  // A virtualização do antd exige altura em PX, e mede-se o PAI (o corpo do
  // drawer, que tem altura real): medir o próprio wrapper dá 0 — ele fica dentro
  // de um container com `overflow-y: auto`, onde a altura é a do conteúdo, e o
  // conteúdo é a lista virtual, que sem altura não renderiza NADA. Era por isso
  // que a árvore aparecia vazia e sem resposta ao clique.
  // Depende de `hasTree`: o drawer monta antes do data model, com o EmptyState no
  // lugar do wrapper — medir só na montagem deixava a altura em 0 para sempre e a
  // virtualização desligada.
  const hasTree = tree.length > 0;
  useEffect(() => {
    const parent = wrapperRef.current?.parentElement;
    if (!parent) { return; }
    // Desconta o padding do wrapper: a lista virtual dimensiona a si mesma, e
    // usar a altura cheia do pai a faria estourar o container por 32px.
    const measure = () => setHeight(Math.max(0, parent.clientHeight - TREE_PADDING * 2));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [hasTree]);

  // A lista virtual trata todo wheel como vertical e dá preventDefault, inclusive
  // o horizontal do trackpad e o shift+roda. Interceptado antes dela, na captura.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) { return; }
    const onWheel = (event: WheelEvent) => {
      const dx = event.shiftKey
        ? event.deltaX || event.deltaY
        : Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
      const holder = wrapper.querySelector<HTMLElement>('.ant-tree-list-holder');
      if (!dx || !holder || holder.scrollWidth <= holder.clientWidth) { return; }
      holder.scrollLeft += dx;
      event.preventDefault();
      event.stopPropagation();
    };
    wrapper.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel, { capture: true });
  }, [hasTree]);

  const visibleTree = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? filterTree(tree, term) : tree;
  }, [tree, search]);

  // Índice por chave: o antd devolve só a chave no onSelect/onLoad.
  const nodesByKey = useMemo(() => {
    const index = new Map<string, BimTreeNode>();
    const walk = (node: BimTreeNode) => {
      index.set(node.key, node);
      node.children.forEach(walk);
    };
    visibleTree.forEach(walk);
    Object.values(elementNodes).forEach((nodes) => nodes.forEach(walk));
    return index;
  }, [visibleTree, elementNodes]);

  /**
   * Materializa as folhas de um `type-group` na hora de expandir. A árvore vem
   * do motor só com os ids; buscar os nomes de tudo de antemão seria carregar
   * centenas de milhares de rótulos que ninguém vai abrir.
   */
  const loadElements = useCallback(async (key: Key) => {
    const groupKey = String(key);
    const group = nodesByKey.get(groupKey);
    if (!group || group.kind !== 'type-group' || elementNodes[groupKey]) { return; }

    const elementIds = group.expressIds ?? [];
    const partsOf = group.parts ?? {};
    const labels = await handle?.getEntityLabels(
      [...elementIds, ...Object.values(partsOf).flat()],
      modelIndex,
    ) ?? [];
    const nameOf = new Map(labels.map(({ expressId, name }) => [expressId, name]));
    const leaf = (expressId: number, ifcType: string, children: BimTreeNode[] = []): BimTreeNode => ({
      key: elementTreeKey(expressId),
      kind: 'element',
      name: nameOf.get(expressId) ?? `#${expressId}`,
      ifcType,
      expressId,
      elementCount: 1,
      children,
    });
    setElementNodes((current) => ({
      ...current,
      // As peças entram como filhas do elemento dono, sem classe própria.
      [groupKey]: elementIds.map((id) => leaf(id, group.ifcType, (partsOf[id] ?? []).map((part) => leaf(part, '')))),
    }));
  }, [handle, modelIndex, nodesByKey, elementNodes]);

  // Revela a seleção vinda da cena. A folha do elemento só existe depois que o
  // grupo carrega, e o rc-tree só carrega grupo montado — na lista virtual, o
  // grupo pode estar fora da tela. Rola uma vez por chave, para não brigar com o
  // scroll do usuário a cada expansão.
  const revealKey = selectedKeys?.[0];
  const revealedKey = useRef<string>();
  useEffect(() => {
    if (!revealKey) { revealedKey.current = undefined; return; }
    if (revealedKey.current === revealKey) { return; }
    if (!nodesByKey.has(revealKey)) {
      const group = expandedKeys
        .map((key) => nodesByKey.get(key))
        .find((node) => node?.kind === 'type-group' && groupHas(node, revealKey));
      if (group) { loadElements(group.key); }
      return;
    }
    revealedKey.current = revealKey;
    treeRef.current?.scrollTo({ key: revealKey, align: 'auto' });
  }, [revealKey, nodesByKey, expandedKeys, loadElements]);

  const visibilityOf = useCallback((node: BimTreeNode): NodeVisibility => {
    if (node.kind === 'element') {
      const ids = [node, ...node.children].map((item) => item.expressId);
      const hidden = ids.filter((id) => id !== undefined && hiddenIds.has(id)).length;
      if (hidden === 0) { return 'visible'; }
      return hidden === ids.length ? 'hidden' : 'partial';
    }
    const hidden = hiddenCountByKey.get(node.key) ?? 0;
    if (hidden === 0) { return 'visible'; }
    return hidden >= node.elementCount ? 'hidden' : 'partial';
  }, [hiddenCountByKey, hiddenIds]);

  // O toggle do hook troca de identidade a cada seleção; passá-lo direto ao
  // contexto re-renderizaria todas as linhas montadas a cada clique na cena.
  const toggleRef = useRef(onToggleVisibility);
  useEffect(() => { toggleRef.current = onToggleVisibility; }, [onToggleVisibility]);
  const toggleVisibility = useCallback((node: BimTreeNode) => toggleRef.current(node), []);
  const isolationRef = useRef(onToggleIsolation);
  useEffect(() => { isolationRef.current = onToggleIsolation; }, [onToggleIsolation]);
  const toggleIsolation = useCallback((node: BimTreeNode) => isolationRef.current(node), []);
  const isIsolated = useCallback((node: BimTreeNode) => isolatedKeys.includes(node.key), [isolatedKeys]);

  const visibility = useMemo<INodeVisibility>(
    () => ({ visibilityOf, toggleVisibility, isIsolated, toggleIsolation }),
    [visibilityOf, toggleVisibility, isIsolated, toggleIsolation],
  );

  // Só a ESTRUTURA entra aqui: visibilidade vai pelo contexto justamente para
  // que esconder um nó não reconstrua (nem faça o antd reprocessar) a árvore toda.
  const toDataNode = useCallback((node: BimTreeNode): DataNode => {
    const title = <NodeTitle node={node} />;

    if (node.kind === 'element') {
      return node.children.length > 0
        ? { key: node.key, title, isLeaf: false, children: node.children.map(toDataNode) }
        : { key: node.key, title, isLeaf: true };
    }

    if (node.kind === 'type-group') {
      return {
        key: node.key,
        title,
        isLeaf: false,
        children: elementNodes[node.key]?.map(toDataNode),
      };
    }

    return {
      key: node.key,
      title,
      children: node.children.map(toDataNode),
      isLeaf: node.children.length === 0,
    };
  }, [elementNodes]);

  const treeData = useMemo(() => visibleTree.map(toDataNode), [visibleTree, toDataNode]);

  const handleSelect = useCallback((keys: Key[]) => {
    const node = keys.length > 0 ? nodesByKey.get(String(keys[0])) : undefined;
    if (node) { onSelectNode(node); }
  }, [nodesByKey, onSelectNode]);

  if (!hasTree) {
    return (
      <EmptyState>
        {state === 'loading' ? t('bim3dTree.loading') : t('bim3dTree.empty')}
      </EmptyState>
    );
  }

  return (
    <TreeWrapper ref={wrapperRef}>
      <NodeVisibilityProvider value={visibility}>
        <Tree
          ref={treeRef}
          treeData={treeData}
          // Só virtualiza depois de ter altura real; `height={0}` não desenha nada.
          height={height || undefined}
          blockNode
          showLine={false}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys ?? []}
          loadData={(node) => loadElements(node.key)}
          onExpand={(keys) => onExpandedKeysChange(keys.map(String))}
          onSelect={handleSelect}
        />
      </NodeVisibilityProvider>
    </TreeWrapper>
  );
};
